import { useMemo, useState, type DragEvent } from 'react';
import {
  ExternalLinkIcon,
  CloudIcon,
  FilePenLineIcon,
  FileTextIcon,
  Loader2Icon,
  MoreVerticalIcon,
  SheetIcon,
  Trash2Icon,
  UploadCloudIcon,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DocumentDTO, DocumentOrigin } from '@shared/schemas/document';
import {
  ACCEPTED_DOCUMENT_EXTENSIONS as ACCEPTED_EXTENSIONS,
  ACCEPTED_DOCUMENT_MIME_TYPES as ACCEPTED_MIMES,
} from '@shared/schemas/document';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { getChatErrorMessage } from '@/features/chat/chat-errors';
import { cn } from '@/lib/utils';
import {
  deleteIndexedDocument,
  getDocumentContentUrl,
  listIndexedDocuments,
  uploadIndexedDocument,
} from '../services/documents.api';
import { collectFilesFromDataTransfer, partitionFiles } from '../lib/folder-traversal';

const documentQueryKey = ['documents', 'indexed-list'] as const;

const PDF_MIME = 'application/pdf';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const SPREADSHEET_MIMES = new Set<string>([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
  'text/tab-separated-values',
]);
const ACCEPT_ATTR = [...ACCEPTED_MIMES, ...ACCEPTED_EXTENSIONS].join(',');
const MAX_PARALLEL_UPLOADS = 3;

type UploadQueueItemStatus = 'queued' | 'uploading' | 'uploaded' | 'failed';
type UploadQueueItem = {
  errorMessage?: string;
  id: string;
  name: string;
  status: UploadQueueItemStatus;
};

function formatDate(value: string) {
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} o`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} Ko`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function documentMetaLine(document: DocumentDTO) {
  const parts: string[] = [formatSize(document.sizeBytes), formatDate(document.updatedAt)];

  if (typeof document.pageCount === 'number') {
    parts.unshift(`${document.pageCount} p.`);
  }

  return parts.join(' · ');
}

function documentUploaderLine(document: DocumentDTO) {
  if (document.uploadedByName) {
    return `Ajouté par ${document.uploadedByName}`;
  }

  if (document.uploadedByEmail) {
    return `Ajouté par ${document.uploadedByEmail}`;
  }

  return null;
}

function statusLabel(document: DocumentDTO) {
  switch (document.status) {
    case 'pending':
      return 'En attente';
    case 'uploading':
      return 'Envoi';
    case 'indexing':
      return 'Indexation';
    case 'completed':
      return 'Indexé';
    case 'failed':
      return 'Échec';
    default:
      return document.status;
  }
}

function DocumentStatusBadge({ document }: { document: DocumentDTO }) {
  const label = statusLabel(document);
  const isBusy = document.status === 'pending' || document.status === 'uploading' || document.status === 'indexing';

  const variantClass = (() => {
    switch (document.status) {
      case 'completed':
        return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100';
      case 'failed':
        return '';
      case 'pending':
        return 'text-muted-foreground';
      case 'indexing':
      case 'uploading':
        return 'border-amber-500/25 bg-amber-500/10 text-amber-950 dark:text-amber-50';
      default:
        return 'text-muted-foreground';
    }
  })();

  return (
    <Badge
      variant={document.status === 'failed' ? 'destructive' : 'outline'}
      data-testid={`document-status-${document.id}`}
      className={cn(
        'shrink-0 gap-1 font-medium',
        document.status === 'completed' && variantClass,
        (document.status === 'uploading' || document.status === 'indexing') && variantClass,
        document.status === 'pending' && variantClass,
      )}
    >
      {isBusy ? (
        <Loader2Icon className="size-3 animate-spin" aria-hidden />
      ) : null}
      {label}
    </Badge>
  );
}

function DocumentFilePicto({ mimeType }: { mimeType: string }) {
  const isPdf = mimeType === PDF_MIME;
  const isWord = mimeType === DOCX_MIME;

  if (isPdf) {
    return (
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-rose-500/12 text-rose-700 dark:text-rose-300"
        title="PDF"
      >
        <FileTextIcon className="size-4" aria-hidden />
      </div>
    );
  }

  if (isWord) {
    return (
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-600/12 text-blue-800 dark:text-blue-300"
        title="Word"
      >
        <FilePenLineIcon className="size-4" aria-hidden />
      </div>
    );
  }

  if (SPREADSHEET_MIMES.has(mimeType)) {
    return (
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/12 text-emerald-700 dark:text-emerald-300"
        title="Tableur"
      >
        <SheetIcon className="size-4" aria-hidden />
      </div>
    );
  }

  return (
    <div
      className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted/80 text-muted-foreground"
      title="Document"
    >
      <FileTextIcon className="size-4" aria-hidden />
    </div>
  );
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [origin, setOrigin] = useState<'all' | DocumentOrigin>('all');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const documentsQuery = useQuery({
    queryFn: () => listIndexedDocuments({
      page: 1,
      pageSize: 50,
      origin: origin === 'all' ? undefined : origin,
      search: search.trim() || undefined,
    }),
    queryKey: [...documentQueryKey, search, origin],
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const hasPendingIndexation = items.some((document) => (
        document.status === 'pending' || document.status === 'indexing' || document.status === 'uploading'
      ));

      return hasPendingIndexation ? 4_000 : false;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteIndexedDocument(documentId),
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
    onSuccess: async () => {
      toast.success('Document supprimé.');
      await queryClient.invalidateQueries({ queryKey: documentQueryKey });
    },
  });

  const viewMutation = useMutation({
    mutationFn: async (documentId: string) => getDocumentContentUrl(documentId),
    onSuccess: (contentUrl) => {
      window.open(contentUrl, '_blank', 'noopener,noreferrer');
    },
  });

  const documents = useMemo(() => documentsQuery.data?.items ?? [], [documentsQuery.data?.items]);
  const isBatchUploading = uploadQueue.some((item) => item.status === 'queued' || item.status === 'uploading');

  function updateQueueItemStatus(itemId: string, updater: (item: UploadQueueItem) => UploadQueueItem) {
    setUploadQueue((current) => current.map((item) => (item.id === itemId ? updater(item) : item)));
  }

  async function runUploadQueue(files: File[]) {
    if (files.length === 0) {
      return;
    }

    const queueEntries: UploadQueueItem[] = files.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      status: 'queued',
    }));
    const entriesByIndex = queueEntries.map((entry, index) => ({ entry, file: files[index]! }));
    setUploadQueue((current) => [...entriesByIndex.map((item) => item.entry), ...current].slice(0, 20));

    let successCount = 0;
    let failureCount = 0;
    let cursor = 0;

    async function runUploadWorker() {
      while (cursor < entriesByIndex.length) {
        const currentIndex = cursor;
        cursor += 1;
        const currentEntry = entriesByIndex[currentIndex];
        if (!currentEntry) {
          continue;
        }

        updateQueueItemStatus(currentEntry.entry.id, (item) => ({ ...item, status: 'uploading' }));
        try {
          await uploadIndexedDocument(currentEntry.file);
          successCount += 1;
          updateQueueItemStatus(currentEntry.entry.id, (item) => ({ ...item, status: 'uploaded' }));
        } catch (error) {
          failureCount += 1;
          updateQueueItemStatus(currentEntry.entry.id, (item) => ({
            ...item,
            errorMessage: getChatErrorMessage(error),
            status: 'failed',
          }));
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_UPLOADS, entriesByIndex.length) }, async () => {
      await runUploadWorker();
    }));

    await queryClient.invalidateQueries({ queryKey: documentQueryKey });

    if (successCount > 0 && failureCount === 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} envoyé${successCount > 1 ? 's' : ''} pour indexation.`);
      return;
    }

    if (successCount > 0 && failureCount > 0) {
      toast.warning(`${successCount} succès, ${failureCount} échec${failureCount > 1 ? 's' : ''} pendant l’envoi.`);
      return;
    }

    toast.error(`${failureCount} échec${failureCount > 1 ? 's' : ''} pendant l’envoi.`);
  }

  async function processFiles(rawFiles: File[]) {
    if (rawFiles.length === 0) {
      return;
    }

    const { acceptedFiles, ignoredCount: ignored } = partitionFiles(rawFiles);

    if (acceptedFiles.length === 0) {
      if (ignored > 0) {
        toast.warning(`Aucun fichier compatible trouvé. ${ignored} fichier(s) ignoré(s).`);
      }
      return;
    }

    if (ignored > 0) {
      toast.info(`${ignored} fichier(s) ignoré(s) (format non supporté).`);
    }

    await runUploadQueue(acceptedFiles);
  }

  async function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    const files = await collectFilesFromDataTransfer(event.dataTransfer);
    await processFiles(files);
  }

  return (
    <section className="w-full space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Base documentaire</h1>
        <p className="text-sm text-muted-foreground">
          Les dépôts manuels sont partagés dans l’entreprise ; les documents SharePoint conservent leurs droits
          d’accès d’origine. Les formats Office sont convertis en PDF avant indexation.
        </p>
      </div>

      <label
        className={[
          'flex min-h-36 cursor-pointer items-center justify-center rounded-[var(--radius-surface-3xl)] border-2 border-dashed bg-card px-6 py-8 text-center transition',
          isDragging ? 'border-foreground/30 bg-muted/40' : 'border-border/70',
        ].join(' ')}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <input
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid="document-upload-input"
          multiple
          type="file"
          onChange={(event) => {
            void processFiles(Array.from(event.target.files ?? []));
            event.currentTarget.value = '';
          }}
        />
        <div className="flex flex-col items-center gap-3">
          <div
            className={cn(
              'flex size-20 items-center justify-center rounded-3xl border border-border/60 bg-muted/40 text-foreground shadow-sm transition',
              isDragging && 'scale-105 border-foreground/30 bg-muted/70',
            )}
            aria-hidden
          >
            {isBatchUploading ? (
              <Loader2Icon className="size-11 animate-spin text-foreground" />
            ) : (
              <UploadCloudIcon className="size-11 text-foreground/80" strokeWidth={1.5} />
            )}
          </div>
          <p className="text-sm font-medium text-foreground">
            Glissez vos fichiers ou un dossier ici, ou cliquez pour sélectionner
          </p>
          <p className="max-w-prose text-xs text-muted-foreground">
            Formats acceptés : PDF, DOCX, XLSX, XLS, ODS, CSV, TSV. Les dossiers déposés sont parcourus récursivement
            (sous-dossiers inclus) ; les fichiers non supportés sont ignorés.
          </p>
        </div>
      </label>

      <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-card p-5">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-semibold text-foreground">Documents indexés</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ToggleGroup
              aria-label="Filtrer par origine"
              type="single"
              value={origin}
              variant="outline"
              spacing={0}
              onValueChange={(value) => value && setOrigin(value as 'all' | DocumentOrigin)}
            >
              <ToggleGroupItem value="all">Tous</ToggleGroupItem>
              <ToggleGroupItem value="manual">Dépôt manuel</ToggleGroupItem>
              <ToggleGroupItem value="m365_sharepoint">SharePoint</ToggleGroupItem>
            </ToggleGroup>
            <Input
              className="max-w-xs"
              placeholder="Rechercher un document"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {documentsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            Chargement des documents…
          </div>
        ) : documents.length === 0 && origin === 'm365_sharepoint' ? (
          <div className="relative overflow-hidden rounded-[var(--radius-surface-3xl)] border border-border/70 bg-muted/30 px-6 py-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-16 left-1/2 size-56 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
            />
            <div className="relative flex flex-col items-center gap-4 text-center">
              <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-background text-primary ring-1 ring-border/70">
                <CloudIcon className="size-7" />
              </span>
              <div className="space-y-1.5">
                <div className="flex items-center justify-center gap-2">
                  <h3 className="text-base font-semibold text-foreground">
                    Connexion Microsoft&nbsp;365
                  </h3>
                  <Badge>Bientôt</Badge>
                </div>
                <p className="mx-auto max-w-md text-sm leading-6 text-muted-foreground">
                  Vos bibliothèques SharePoint se synchroniseront ici automatiquement, et
                  leurs documents deviendront interrogeables par l’assistant.
                </p>
              </div>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun document indexé pour le moment.</p>
        ) : (
          <ul className="grid list-none grid-cols-2 gap-3 p-0 sm:gap-4">
            {documents.map((document) => {
              const isOpening = viewMutation.isPending && viewMutation.variables === document.id;
              const isDeleting = deleteMutation.isPending && deleteMutation.variables === document.id;
              const menuBusy = isOpening || isDeleting;

              return (
                <li key={document.id}>
                  <Card size="sm" className="h-full border-border/80 shadow-none ring-1 ring-border/60">
                    <CardHeader className="gap-0 pb-4">
                      <div className="flex items-start gap-2.5">
                        <DocumentFilePicto mimeType={document.mimeType} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-1.5">
                            <CardTitle className="line-clamp-2 min-w-0 flex-1 pt-0.5 text-sm font-semibold leading-snug">
                              {document.name}
                            </CardTitle>
                            <div className="flex shrink-0 items-center gap-1">
                              <Badge variant="secondary" className="gap-1">
                                {document.origin === 'm365_sharepoint'
                                  ? <CloudIcon className="size-3" />
                                  : <UploadCloudIcon className="size-3" />}
                                {document.origin === 'm365_sharepoint' ? 'SharePoint' : 'Dépôt manuel'}
                              </Badge>
                              <DocumentStatusBadge document={document} />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-8 text-muted-foreground hover:text-foreground"
                                    disabled={menuBusy}
                                    aria-label={`Actions pour ${document.name}`}
                                  >
                                    {menuBusy ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <MoreVerticalIcon className="size-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-44">
                                  <DropdownMenuItem
                                    disabled={!document.hasSourceFile || isOpening}
                                    onClick={() => void viewMutation.mutate(document.id)}
                                  >
                                    {isOpening ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <ExternalLinkIcon className="size-4" />
                                    )}
                                    Consulter
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    variant="destructive"
                                    disabled={isDeleting}
                                    onClick={() => void deleteMutation.mutateAsync(document.id)}
                                  >
                                    {isDeleting ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <Trash2Icon className="size-4" />
                                    )}
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                            {documentMetaLine(document)}
                          </p>
                          {documentUploaderLine(document) ? (
                            <p className="text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                              {documentUploaderLine(document)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
